import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';

interface NotificationItem {
  id: number; type: string; title: string; body: string;
  action_type: string | null; action_id: number | null;
  is_read: boolean; read_at: string | null; created_at: string;
}

const typeConfig: Record<string, { icon: string; color: string; bg: string }> = {
  welcome: { icon: '\ud83d\udc4b', color: '#16a34a', bg: '#f0fdf4' },
  verification: { icon: '\ud83d\udee1\ufe0f', color: '#2563eb', bg: '#eff6ff' },
  ad_status: { icon: '\ud83d\udce2', color: '#ea580c', bg: '#fff7ed' },
  review: { icon: '\u2b50', color: '#ca8a04', bg: '#fefce8' },
  message: { icon: '\ud83d\udcac', color: '#7c3aed', bg: '#f5f3ff' },
  system: { icon: '\ud83d\udd14', color: '#64748b', bg: '#f8fafc' },
  general: { icon: '\ud83d\udd14', color: '#64748b', bg: '#f8fafc' },
};

const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const navigate = useNavigate();
  const hasToken = Boolean(localStorage.getItem('user'));

  const fetchNotifications = useCallback(async (p: number) => {
    try {
      setLoading(true);
      const res = await api.get('/notifications', { params: { page: p, per_page: 20 } });
      const d = res.data.data;
      setNotifications(d.notifications || []);
      setUnreadCount(d.unread_count || 0);
      setLastPage(d.pagination?.last_page || 1);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (hasToken) { fetchNotifications(page); } else { navigate('/login'); }
  }, [hasToken, page, fetchNotifications, navigate]);

  const markAsRead = async (id: number) => {
    try {
      await api.patch('/notifications/' + id + '/read', {});
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) { console.error(e); }
  };

  const markAllRead = async () => {
    try {
      await api.patch('/notifications/read-all', {});
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('\u062a\u0645 \u062a\u0639\u0644\u064a\u0645 \u0627\u0644\u0643\u0644 \u0643\u0645\u0642\u0631\u0648\u0621');
    } catch (e) { console.error(e); }
  };

  const onNotifClick = (n: NotificationItem) => {
    if (n.is_read === false) markAsRead(n.id);
    if (n.action_type === 'ad' && n.action_id) navigate('/ads/' + n.action_id);
    else if (n.action_type === 'user' && n.action_id) navigate('/users/' + n.action_id);
  };

  const ago = (d: string) => {
    const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
    if (s < 60) return '\u0627\u0644\u0622\u0646';
    if (s < 3600) return '\u0645\u0646\u0630 ' + Math.floor(s/60) + ' \u062f';
    if (s < 86400) return '\u0645\u0646\u0630 ' + Math.floor(s/3600) + ' \u0633';
    if (s < 2592000) return '\u0645\u0646\u0630 ' + Math.floor(s/86400) + ' \u064a\u0648\u0645';
    return '\u0645\u0646\u0630 ' + Math.floor(s/2592000) + ' \u0634\u0647\u0631';
  };

  if (loading) return (
    <div style={{padding:'60px 16px',textAlign:'center',fontFamily:"'Cairo',sans-serif"}}>
      <p style={{color:'#9E9E9E',fontSize:14}}>{'\u062c\u0627\u0631\u064a \u0627\u0644\u062a\u062d\u0645\u064a\u0644...'}</p>
    </div>
  );

  return (
    <div style={{fontFamily:"'Cairo',sans-serif"}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
        <div>
          <h1 style={{fontSize:22,fontWeight:700,color:'#212121',margin:0}}>{'\ud83d\udd14 \u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062a'}</h1>
          {unreadCount > 0 && <p style={{fontSize:13,color:'#9E9E9E',margin:'4px 0 0'}}>{unreadCount} {'\u0625\u0634\u0639\u0627\u0631 \u063a\u064a\u0631 \u0645\u0642\u0631\u0648\u0621'}</p>}
        </div>
        {unreadCount > 0 && <button onClick={markAllRead} style={{background:'#E6F2F0',color:'#005343',border:'none',padding:'8px 16px',borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:"'Cairo',sans-serif"}}>{'\u062a\u0639\u0644\u064a\u0645 \u0627\u0644\u0643\u0644 \u0645\u0642\u0631\u0648\u0621 \u2713'}</button>}
      </div>

      {notifications.length === 0 ? (
        <div style={{background:'#fff',borderRadius:12,border:'1px solid #E0E0E0',padding:'60px 20px',textAlign:'center'}}>
          <span style={{fontSize:48}}>{'\ud83d\udd15'}</span>
          <p style={{color:'#9E9E9E',fontSize:15,marginTop:12}}>{'\u0644\u0627 \u062a\u0648\u062c\u062f \u0625\u0634\u0639\u0627\u0631\u0627\u062a'}</p>
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {notifications.map(n => {
            const t = typeConfig[n.type] || typeConfig.general;
            const hasAction = n.action_type !== null && n.action_id !== null;
            return (
              <div key={n.id} onClick={() => onNotifClick(n)} style={{
                background: n.is_read ? '#fff' : t.bg, borderRadius:12,
                border: n.is_read ? '1px solid #E0E0E0' : '1px solid ' + t.color + '30',
                padding:'16px 20px', cursor: hasAction ? 'pointer' : 'default',
                display:'flex', gap:14, alignItems:'flex-start', position:'relative',
              }}>
                <div style={{width:44,height:44,borderRadius:'50%',background:n.is_read?'#F5F5F5':t.color+'15',display:'flex',alignItems:'center',justifyContent:'center',fontSize:20,flexShrink:0}}>{t.icon}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:8}}>
                    <h3 style={{fontSize:14,fontWeight:n.is_read?500:700,color:n.is_read?'#616161':'#212121',margin:0}}>{n.title}</h3>
                    <span style={{fontSize:11,color:'#BDBDBD',whiteSpace:'nowrap',flexShrink:0}}>{ago(n.created_at)}</span>
                  </div>
                  <p style={{fontSize:13,color:n.is_read?'#9E9E9E':'#616161',margin:'4px 0 0',lineHeight:1.6}}>{n.body}</p>
                  {hasAction && <span style={{fontSize:11,color:t.color,fontWeight:600,marginTop:6,display:'inline-block'}}>{'\u0639\u0631\u0636 \u0627\u0644\u062a\u0641\u0627\u0635\u064a\u0644 \u2190'}</span>}
                </div>
                {n.is_read === false && <div style={{position:'absolute',top:8,left:8,width:8,height:8,borderRadius:'50%',background:t.color}} />}
              </div>
            );
          })}
        </div>
      )}

      {lastPage > 1 && (
        <div style={{display:'flex',justifyContent:'center',gap:8,marginTop:20}}>
          <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1} style={{padding:'8px 16px',borderRadius:8,border:'1px solid #E0E0E0',background:'#fff',fontFamily:"'Cairo',sans-serif",fontSize:13,opacity:page===1?0.5:1,cursor:page===1?'default':'pointer'}}>{'\u0627\u0644\u0633\u0627\u0628\u0642'}</button>
          <span style={{padding:'8px 12px',fontSize:13,color:'#757575'}}>{page} / {lastPage}</span>
          <button onClick={() => setPage(p => Math.min(lastPage,p+1))} disabled={page===lastPage} style={{padding:'8px 16px',borderRadius:8,border:'1px solid #E0E0E0',background:'#fff',fontFamily:"'Cairo',sans-serif",fontSize:13,opacity:page===lastPage?0.5:1,cursor:page===lastPage?'default':'pointer'}}>{'\u0627\u0644\u062a\u0627\u0644\u064a'}</button>
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
